/**
 * Stripe Pre-warming Service
 * 
 * This singleton pre-loads Stripe.js and creates PaymentRequest objects
 * at app startup so Apple Pay / Google Pay buttons appear instantly.
 * 
 * Key optimizations:
 * 1. Caches publishable key in localStorage to skip edge function call on repeat visits
 * 2. Pre-loads Stripe.js immediately when this module is imported
 * 3. Pre-creates a template PaymentRequest that can be cloned/updated
 * 4. Registers Apple Pay domain once and caches the result
 */

import { supabase } from '@/integrations/supabase/client';

// Cache keys
const STRIPE_KEY_CACHE = 'stripe_publishable_key';
const STRIPE_KEY_CACHE_TIME = 'stripe_key_cached_at';
const APPLE_PAY_DOMAIN_REGISTERED = 'apple_pay_domain_registered';

// Cache duration: 24 hours (key rarely changes)
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000;

// Module-level singletons
let stripeModulePromise: Promise<typeof import('@stripe/stripe-js')> | null = null;
let stripeReactModulePromise: Promise<typeof import('@stripe/react-stripe-js')> | null = null;
let stripeInstancePromise: Promise<import('@stripe/stripe-js').Stripe | null> | null = null;
let publishableKey: string | null = null;
let isPreloading = false;
let preloadComplete = false;

// Pre-created PaymentRequest template (for default annual plan)
let templatePaymentRequest: import('@stripe/stripe-js').PaymentRequest | null = null;
let canMakePaymentResult: { applePay?: boolean; googlePay?: boolean } | null = null;

/**
 * Get cached publishable key or fetch from edge function
 */
async function getPublishableKey(): Promise<string | null> {
  // Check memory cache first
  if (publishableKey) {
    return publishableKey;
  }

  // Check localStorage cache
  try {
    const cachedKey = localStorage.getItem(STRIPE_KEY_CACHE);
    const cachedTime = localStorage.getItem(STRIPE_KEY_CACHE_TIME);
    
    if (cachedKey && cachedTime) {
      const age = Date.now() - parseInt(cachedTime, 10);
      if (age < CACHE_DURATION_MS) {
        console.log('[StripePreloader] Using cached publishable key');
        publishableKey = cachedKey;
        return cachedKey;
      }
    }
  } catch (e) {
    // localStorage might not be available
  }

  // Fetch from edge function
  console.log('[StripePreloader] Fetching publishable key from server...');
  const { data, error } = await supabase.functions.invoke('get-stripe-config');
  
  if (error || !data?.publishableKey) {
    console.error('[StripePreloader] Failed to get publishable key:', error);
    return null;
  }

  publishableKey = data.publishableKey;

  // Cache in localStorage
  try {
    localStorage.setItem(STRIPE_KEY_CACHE, data.publishableKey);
    localStorage.setItem(STRIPE_KEY_CACHE_TIME, Date.now().toString());
  } catch (e) {
    // Ignore storage errors
  }

  return data.publishableKey;
}

/**
 * Register Apple Pay domain (only once per domain)
 */
async function registerApplePayDomain(): Promise<void> {
  const domain = window.location.hostname;
  const cacheKey = `${APPLE_PAY_DOMAIN_REGISTERED}_${domain}`;

  try {
    const registered = localStorage.getItem(cacheKey);
    if (registered === 'true') {
      console.log('[StripePreloader] Apple Pay domain already registered');
      return;
    }
  } catch (e) {
    // Ignore
  }

  try {
    await supabase.functions.invoke('ensure-apple-pay-domain', {
      body: { domain }
    });
    console.log('[StripePreloader] Apple Pay domain registered');
    
    try {
      localStorage.setItem(cacheKey, 'true');
    } catch (e) {
      // Ignore
    }
  } catch (e) {
    console.warn('[StripePreloader] Apple Pay domain registration failed:', e);
  }
}

/**
 * Load Stripe.js module (cached)
 */
export function getStripeModule(): Promise<typeof import('@stripe/stripe-js')> {
  if (!stripeModulePromise) {
    stripeModulePromise = import('@stripe/stripe-js');
  }
  return stripeModulePromise;
}

/**
 * Load Stripe React module (cached)
 */
export function getStripeReactModule(): Promise<typeof import('@stripe/react-stripe-js')> {
  if (!stripeReactModulePromise) {
    stripeReactModulePromise = import('@stripe/react-stripe-js');
  }
  return stripeReactModulePromise;
}

/**
 * Get or create Stripe instance (cached)
 */
export async function getStripeInstance(): Promise<import('@stripe/stripe-js').Stripe | null> {
  if (stripeInstancePromise) {
    return stripeInstancePromise;
  }

  stripeInstancePromise = (async () => {
    const key = await getPublishableKey();
    if (!key) return null;

    const stripeModule = await getStripeModule();
    return stripeModule.loadStripe(key);
  })();

  return stripeInstancePromise;
}

/**
 * Get cached publishable key (sync, returns null if not cached yet)
 */
export function getCachedPublishableKey(): string | null {
  return publishableKey;
}

/**
 * Get pre-created PaymentRequest result
 */
export function getPreloadedPaymentResult() {
  return {
    paymentRequest: templatePaymentRequest,
    canMakePayment: canMakePaymentResult,
    isReady: preloadComplete,
  };
}

/**
 * Pre-warm Stripe - call this at app startup
 * This runs in the background without blocking the UI
 */
export async function preloadStripe(): Promise<void> {
  if (isPreloading || preloadComplete) {
    return;
  }
  isPreloading = true;

  console.log('[StripePreloader] Starting pre-warm...');
  const startTime = performance.now();

  try {
    // Parallel load: Stripe modules + publishable key + Apple Pay domain
    const [stripeModule, , key] = await Promise.all([
      getStripeModule(),
      getStripeReactModule(),
      getPublishableKey(),
      registerApplePayDomain(),
    ]);

    if (!key) {
      console.warn('[StripePreloader] No publishable key, cannot pre-create PaymentRequest');
      preloadComplete = true;
      return;
    }

    // Load Stripe instance
    const stripe = await stripeModule.loadStripe(key);
    if (!stripe) {
      console.warn('[StripePreloader] Stripe failed to load');
      preloadComplete = true;
      return;
    }

    // Store the promise for later use
    stripeInstancePromise = Promise.resolve(stripe);

    // Create a template PaymentRequest with default annual price
    // This will be updated with actual price when modal opens
    const pr = stripe.paymentRequest({
      country: 'US', // Will be updated when used
      currency: 'usd', // Will be updated when used
      total: {
        label: 'Subscription',
        amount: 4788, // Default annual price in cents, will be updated
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    // Check if device can make payment
    const result = await pr.canMakePayment();
    console.log('[StripePreloader] canMakePayment result:', result);

    if (result) {
      templatePaymentRequest = pr;
      canMakePaymentResult = result;
    }

    const elapsed = Math.round(performance.now() - startTime);
    console.log(`[StripePreloader] Pre-warm complete in ${elapsed}ms`);
  } catch (e) {
    console.error('[StripePreloader] Pre-warm error:', e);
  } finally {
    preloadComplete = true;
    isPreloading = false;
  }
}

/**
 * Create a fresh PaymentRequest with specific parameters
 * Uses cached Stripe instance for speed
 */
export async function createPaymentRequest(options: {
  country: string;
  currency: string;
  amount: number;
  label: string;
}): Promise<{
  paymentRequest: import('@stripe/stripe-js').PaymentRequest | null;
  canMakePayment: { applePay?: boolean; googlePay?: boolean } | null;
  stripe: import('@stripe/stripe-js').Stripe | null;
}> {
  const stripe = await getStripeInstance();
  
  if (!stripe) {
    return { paymentRequest: null, canMakePayment: null, stripe: null };
  }

  const pr = stripe.paymentRequest({
    country: options.country,
    currency: options.currency.toLowerCase(),
    total: {
      label: options.label,
      amount: options.amount,
    },
    requestPayerName: true,
    requestPayerEmail: true,
  });

  const result = await pr.canMakePayment();

  return {
    paymentRequest: result ? pr : null,
    canMakePayment: result,
    stripe,
  };
}

// Start pre-warming immediately when this module is imported
// This happens during the initial app bundle evaluation
if (typeof window !== 'undefined') {
  // Use requestIdleCallback for non-blocking preload
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(() => preloadStripe(), { timeout: 2000 });
  } else {
    // Fallback: delay slightly to not block initial render
    setTimeout(() => preloadStripe(), 100);
  }
}
